import { Anchor } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { EntityType } from '@repo/common/enums/entityType';
import { useCallback } from 'react';
import type { SecodaProperties } from '../../../api';
import { useCreateQuestion } from '../../../api';
import { resourceCatalogQueryKeyFactory } from '../../../api/hooks/resourceCatalog/constants';
import queryClient from '../../../api/queryClient';
import type { CreateEntityModalOnCreateEntityFnArgs } from '../types';

export function useCreateEntity() {
	const { mutateAsync: createQuestion } = useCreateQuestion({});

	return useCallback(
		async (args: CreateEntityModalOnCreateEntityFnArgs) => {
			switch (args.entityType) {
				case EntityType.question: {
					const question = await createQuestion({
						data: {
							title: args.title,
							definition: args.description,
							owners: args.metadata?.owners as string[] | undefined,
							assigned_to: args.metadata?.assignee as string | undefined,
							assigned_to_group: args.metadata?.assignee_group as
								| string
								| undefined,
							priority: args.metadata?.priority as string | undefined,
							tags: args.metadata?.tags as string[] | undefined,
							teams: args.teams,
							properties: args.metadata?.properties as
								| SecodaProperties
								| undefined,
							properties2: args.metadata?.properties2 as
								| Record<string, string | string[] | object>
								| undefined,
							ai_prompt:
								args.attachment?.type === 'ai_chat_message'
									? args.attachment.attachmentId
									: undefined,
						},
					});
					// Invalidate resource catalog lists so new questions appear without refresh
					queryClient.invalidateQueries({
						queryKey: resourceCatalogQueryKeyFactory.allLists(),
					});
					showNotification({
						title: 'Question created',
						message: (
							<Anchor
								data-testid="created-notification"
								role="link"
								href={`/questions/${question.id}`}
							>
								View your question
							</Anchor>
						),
						color: 'green',
					});
					break;
				}
				default:
					showNotification({
						title: 'Error',
						message: `${args.entityType} entity type is not supported`,
						color: 'red',
					});
					break;
			}
		},
		[createQuestion]
	);
}
